import moment from "moment";
import * as notify from "shared/notify";
import { get, put, post } from "shared/data";
import { getSortParams } from "shared/helpers";
import { EMPTY_PAGINATION } from "shared/constants";
import { TaskStatus } from "shared/constants/task-status";
import { TaskType } from "shared/taskTypeMap";

const taskCache = {};

export const getTaskContextByKey = (key, taskType) => {
    const hideChildren = taskType === TaskType.PROFILING ? "&hideChildProducts=true" : "";

    return get(`/tasking/contexts?shipment=${key}${hideChildren}`);
};

const APPLICABLE_STATES = {
    ACTIVE: 11,
    COMPLETED: 3,
    PARTIAL_READY: 15,
    READY: 10,
    REDO: 12,
    TO_DO: 1,
};

const applicableStates = [
    {
        stateId: APPLICABLE_STATES.ACTIVE,
        state: TaskStatus.ACTIVE,
        description: "Active",
    },
    {
        stateId: APPLICABLE_STATES.COMPLETED,
        state: TaskStatus.COMPLETED,
        description: "Completed",
    },
    {
        stateId: APPLICABLE_STATES.PARTIAL_READY,
        state: TaskStatus.PARTIAL_READY,
        description: "Partial Ready",
    },
    {
        stateId: APPLICABLE_STATES.READY,
        state: TaskStatus.READY,
        description: "Ready",
    },
    {
        stateId: APPLICABLE_STATES.REDO,
        state: TaskStatus.REDO,
        description: "Redo",
    },
    {
        stateId: APPLICABLE_STATES.TO_DO,
        state: TaskStatus.TO_DO,
        description: "Open",
    },
    {
        stateId: APPLICABLE_STATES.TO_DO,
        state: "open",
        description: "Open",
    },
];

// TODO: refactor and create a hook from this fn after BE filtering endpoints will be refactored
const getNewFilterParams = (filters) => {
    const paramFiltered = filters.reduce((arr, filter) => {
        const mapContains = (name, value) => {
            arr.push(`contains(${name}, '${value.replace(/'/g, "''")}')`);
        };

        const mapListItem = (name, values) => {
            const formattedValues = values.map((v) => `contains(${name}, '${v.replace(/'/g, "''")}')`).join(" or ");
            arr.push(`(${formattedValues})`);
        };

        const mapEquals = (name, value) => {
            arr.push(`${name} EQ ${value}`);
        };

        const mapNotEquals = (name, value) => {
            arr.push(`${name} NE ${value}`);
        };

        const id = filter.id.split(".").shift();

        switch (id) {
            case "shipmentProfilingState":
                if (filter.value === "active") {
                    arr.push(`${id} ne 'completed'`);
                } else {
                    arr.push(`${id} eq '${encodeURIComponent(filter.value)}'`);
                }

                break;
            case "shipmentProfilingStateId":
            case "state":
                const currentState = applicableStates.find(({ state }) => state === filter.value);

                if (!currentState) {
                    break;
                }

                if (currentState.stateId === APPLICABLE_STATES.ACTIVE) {
                    mapNotEquals(id, APPLICABLE_STATES.COMPLETED);
                } else {
                    mapEquals(id, currentState.stateId);
                }

                break;
            case "locationDataSource":
                if (filter.value !== "") {
                    mapEquals(id, `'${filter.value}'`);
                }

                break;
            case "shipmentType":
                if (filter.value !== "-1") {
                    mapEquals(id, filter.value);
                }

                break;
            case "profilingTasksAssignmentPriority":
                mapEquals(id, filter.value);

                break;
            case "carrier":
                mapContains("carrierName", filter.value);

                break;
            case "receivedBy":
                mapContains("receivedByName", filter.value);

                break;
            case "client":
                mapContains("clientName", filter.value);

                break;
            case "packageLevelName":
                mapContains(id, filter.value);

                break;
            case "cxHClient":
                mapContains("cxHClientName", filter.value);
                break;
            case "supplier":
                mapContains("supplierName", filter.value);
                break;
            case "dueOn":
            case "schedulingDueDateTime":
                if (filter.value) {
                    if (filter.value.filterFromDate) {
                        const fromMoment = moment(filter.value.filterFromDate).utc(false).startOf("day").format();
                        arr.push(`${id} gt ${fromMoment}`);
                    }

                    if (filter.value.filterToDate) {
                        const toMoment = moment(filter.value.filterToDate).utc(false).endOf("day").format();
                        arr.push(`${id} lt ${toMoment}`);
                    }
                }

                break;
            case "isRush":
                if (filter.value !== "all") {
                    mapEquals(id, filter.value);
                }

                break;
            case "matchedProductTaskContextKey":
                filter.value === "true" ? arr.push(`'${id}' ne null`) : arr.push(`'${id}' EQ null`);
                break;
            case "profilingTasksAssignmentGroupKey":
                filter.value === "null" ? arr.push(`${id} eq null`) : arr.push(`${id} eq '${encodeURIComponent(filter.value)}'`);
                break;
            case "userId":
            case "fullName":
                filter.value === "null" ? mapEquals(id, "null") : arr.push(`userId eq ${encodeURIComponent(filter.value)}`);
                break;
            case "upc":
                if (filter.values) {
                    mapListItem(id, filter.values);
                }

                break;
            default:
                mapContains(id, filter.value);
        }

        return arr;
    }, []);

    return paramFiltered.length > 0 ? `${paramFiltered.join(" AND ")}` : "";
};

const getFilterParams = (ogFilters, sourcePage) => {
    const filters = ogFilters;
    const minThree = [];
    const paramFiltered = filters.reduce((arr, filter) => {
        const id = filter.id.split(".").pop();
        switch (id) {
            case "state":
                if (filter.value === "active") {
                    arr.push(`${id} ne 'completed'`);
                } else if (filter.value === "all") {
                } else {
                    arr.push(`${id} eq '${encodeURIComponent(filter.value)}'`);
                }

                break;
            case "shipmentType":
                if (filter.value !== "-1") {
                    arr.push(`${id} eq ${filter.value}`);
                }

                break;
            case "locationDataSource":
                if (filter.value !== "") {
                    arr.push(`${id} eq '${filter.value}'`);
                }

                break;
            case "description":
            case "binTrackingNumber":
            case "upc":
            case "client":
            case "packageLevelName":
            case "cxHClient":
                if (filter.value.length > 2) {
                    arr.push(`contains(${id}, '${encodeURIComponent(filter.value).replace(/'/g, "''")}')`);
                } else {
                    minThree.push(id);
                }

                break;
            case "priority":
            case "locationId":
            case "isRush":
            case "hasPremiumEditing":
            case "isFoodService":
            case "hasSupplierCodingAttributes":
            case "imagesNeedClientApproval":
                arr.push(`${id} eq ${encodeURIComponent(filter.value)}`);
                break;
            case "assignmentGroupKey":
                filter.value === "null" ? arr.push(`${id} eq null`) : arr.push(`${id} eq '${encodeURIComponent(filter.value)}'`);
                break;
            case "assignedUserInfo":
            case "fullName":
                filter.value === "null" ? arr.push(`userId eq null`) : arr.push(`userId eq ${encodeURIComponent(filter.value)}`);
                break;
            case "shipmentDueDate":
            case "due":
                if (filter.value) {
                    if (filter.value.filterFromDate) {
                        const fromMoment = moment(filter.value.filterFromDate).utc(false).startOf("day").format();
                        arr.push(`${id} gt ${encodeURIComponent(fromMoment)}`);
                    }

                    if (filter.value.filterToDate) {
                        const toMoment = moment(filter.value.filterToDate).utc(false).endOf("day").format();
                        arr.push(`${id} lt ${encodeURIComponent(toMoment)}`);
                    }
                }

                break;
            case "matchedProductTaskContextKey":
                filter.value === "true" ? arr.push(`${id} ne null`) : arr.push(`${id} eq null`);
                break;
            default:
                if (filter.value.length > 2) {
                    arr.push(`startswith(${id}, '${encodeURIComponent(filter.value.replace(/'/g, "''"))}')`);
                } else {
                    minThree.push(id);
                }
        }

        return arr;
    }, []);

    if (minThree.length > 0) {
        notify.warning(`${minThree.join(", ")} requires 3 characters to filter`);
    }

    return paramFiltered.length > 0 ? `&filter=${paramFiltered.join(" AND ")}` : "";
};

export const filterTask = (pageNumber, pageSize, sorted, filter) => {
    const filterBy = getNewFilterParams(filter);

    return post("/receiving/profiling/pending/search", {
        PagingInfo: {
            PageSize: pageSize,
            PageNumber: pageNumber,
        },
        sortColumns: sorted
            .filter(({ desc }) => typeof desc === "boolean")
            .map(({ id, desc }) => {
                return {
                    columnName: id,
                    sortOrder: desc ? "desc" : "asc",
                };
            }),
        ColumnFilters: filterBy,
    });
};

export const searchTasks = (taskType, body) => {
    if (taskType === "duplicatestaging") {
        return post("/duplicate-match/staging-task/list/search", body);
    }

    return post(`/tasking/tasks/listtype/${taskType}/search`, body);
};

export const getTasks = (type, pageNumber = 1, pageSize, sort, filter, sourcePage) => {
    const sortBy = getSortParams(sort);
    const filterBy = getFilterParams(filter, sourcePage);
    //TODO: encodeURIComponent()
    const url = `/tasking/tasks/listtype/${type}?pagesize=${pageSize}&pagenumber=${pageNumber}${sortBy}${filterBy}`;

    return get(url, { defaultReturn: { ...EMPTY_PAGINATION } });
};

export const getAssignmentOptions = (type) => {
    return get(`/tasking/assignments/groups?taskType=${type}`).then((assignments) => {
        return assignments && assignments.count > 0 && assignments.items
            ? assignments.items.map((ai) => ({
                  name: "assignment",
                  text: ai.groupName,
                  value: ai.groupKey,
                  raw: ai,
              }))
            : [];
    });
};

export const putTaskAssignment = (taskKey, groupKey) => {
    return put(`/tasking/assignments/tasks/${taskKey}`, { GroupKey: groupKey });
};

export const getTaskByKey = (key) => {
    return get(`/tasking/tasks/${key}`);
};

export const getTasksByContextKey = (key, isDuplicateStageProduct) => {
    const isDuplicate = isDuplicateStageProduct ? "?showDuplicateStagedProducts=true" : "";

    return get(`/tasking/tasks/list/${key}${isDuplicate}`);
};

export const getTaskStates = () => {
    // {
    //    "additionalProp1": "string",
    //    "additionalProp2": "string",
    //    "additionalProp3": "string"
    // }
    return get("/tasking/taskstates");
};

export const getTaskTypes = (type, modifier = "standard") => {
    return get(`/tasking/taskTypes/${type}.${modifier}`).then((response) => {
        if (!response) {
            return null;
        }

        response.statusList = response.applicableStates.map((t) => ({ text: t.description, value: t.state }));
        response.statusList.unshift({ text: "Show All", value: "all" });

        return response;
    });
};

export const seedTaskCommentCache = (taskId, comments) => {
    taskCache[taskId] = comments;
};

export const getTaskComments = (taskId) => {
    if (taskCache[taskId]) {
        return Promise.resolve(taskCache[taskId]);
    }

    return get(`/tasking/tasks/${taskId}/comments`).then((comments) => {
        taskCache[taskId] = comments;

        return comments;
    });
};

export const getTaskContextComments = (taskContextKey) => {
    return get(`/tasking/contexts/${taskContextKey}/comments`);
};

export const getLocations = () => {
    return get("/receiving/locations?GetInactiveLocations=true").then((data) => {
        if (!data) {
            return [];
        }

        return data.map((c) => {
            return { text: c.location, value: c.location, companyName: c.companyName };
        });
    });
};

export const getTaskStatusTypes = (type) => {
    return get(`/tasking/taskTypes/${type}`).then((response) => {
        if (!response) {
            return null;
        }

        response.statusList = response.applicableStates.map((t) => ({ text: t.description, value: t.state }));

        return response;
    });
};

export const getTaskTypeRejectReason = () => {
    return get("/task-type-reject-reason-lookup");
};
